import React, { useState, useEffect } from 'react';
import { Card, Form, Select, Switch, Input, Button, Typography, Space, message, Divider } from 'antd';
import { GlobalOutlined, ClockCircleOutlined, SaveOutlined, ReloadOutlined } from '@ant-design/icons';
import axios from 'axios';

const { Title, Text } = Typography;
const { Option } = Select;

const DateTimeSettings = () => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [currentTime, setCurrentTime] = useState(new Date());

    const fetchSettings = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get('/api/settings/time', {
                headers: { Authorization: `Bearer ${token}` }
            });
            form.setFieldsValue(res.data);
        } catch (error) {
            message.error('Failed to load time settings');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSettings();
        const timer = setInterval(() => setCurrentTime(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);

    const onFinish = async (values) => {
        try {
            const token = localStorage.getItem('token');
            await axios.put('/api/settings/time', values, {
                headers: { Authorization: `Bearer ${token}` }
            });
            message.success('System time settings updated successfully');
        } catch (error) {
            message.error('Failed to update settings');
        }
    };

    const commonTimezones = [
        'Asia/Amman', 'Asia/Riyadh', 'Asia/Dubai', 'Asia/Kuwait', 'Asia/Qatar',
        'UTC', 'Europe/London', 'Europe/Paris', 'America/New_York', 'America/Los_Angeles'
    ];

    return (
        <div style={{ padding: '0 24px' }}>
            <div style={{ marginBottom: 24 }}>
                <Title level={2} style={{ margin: 0 }}>Time & Date Settings</Title>
                <Text type="secondary">Configure system timezone, NTP, and display formats</Text>
            </div>

            <Card style={{ maxWidth: 800 }}>
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={onFinish}
                    initialValues={{ ntp_enabled: true, ntp_server: 'pool.ntp.org' }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 24, padding: 16, background: '#f5f5f5', borderRadius: 8 }}>
                        <ClockCircleOutlined style={{ fontSize: 24, marginRight: 16, color: '#1890ff' }} />
                        <div>
                            <Text strong>Current Server Time</Text><br />
                            <Title level={3} style={{ margin: 0 }}>{currentTime.toLocaleString()}</Title>
                        </div>
                    </div>

                    <Title level={4}><GlobalOutlined /> Timezone</Title>
                    <Form.Item name="timezone" label="System Timezone" rules={[{ required: true }]}>
                        <Select showSearch placeholder="Select a timezone">
                            {commonTimezones.map(tz => (
                                <Option key={tz} value={tz}>{tz}</Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Divider />

                    <Title level={4}><ReloadOutlined /> NTP Configuration</Title>
                    <Form.Item name="ntp_enabled" valuePropName="checked">
                        <Switch checkedChildren="NTP Enabled" unCheckedChildren="NTP Disabled" />
                    </Form.Item>

                    <Form.Item name="ntp_server" label="NTP Server Hostname" rules={[{ required: true }]}>
                        <Input placeholder="e.g. pool.ntp.org" />
                    </Form.Item>

                    <Divider />

                    <Title level={4}><ClockCircleOutlined /> Display Formats</Title>
                    <Space size="large">
                        <Form.Item name="time_format" label="Time Format">
                            <Select style={{ width: 150 }}>
                                <Option value="12h">12-hour (AM/PM)</Option>
                                <Option value="24h">24-hour</Option>
                            </Select>
                        </Form.Item>
                        <Form.Item name="date_format" label="Date Format">
                            <Select style={{ width: 200 }}>
                                <Option value="DD/MM/YYYY">DD/MM/YYYY</Option>
                                <Option value="MM/DD/YYYY">MM/DD/YYYY</Option>
                                <Option value="YYYY-MM-DD">YYYY-MM-DD (ISO)</Option>
                            </Select>
                        </Form.Item>
                    </Space>

                    <Form.Item style={{ marginTop: 24 }}>
                        <Button type="primary" htmlType="submit" icon={<SaveOutlined />} size="large" loading={loading}>
                            Save Changes
                        </Button>
                        <Button style={{ marginLeft: 8 }} icon={<ReloadOutlined />} onClick={fetchSettings}>
                            Reset to Defaults
                        </Button>
                    </Form.Item>
                </Form>
            </Card>
        </div>
    );
};

export default DateTimeSettings;
